import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RoomPreviewModal = ({ isOpen, onClose, room }) => {
  const [selectedOverlay, setSelectedOverlay] = useState('none');

  const overlayOptions = [
    { value: 'none', label: 'No Overlay', color: null },
    { value: 'blue', label: 'Blue Base', color: '#2563EB' },
    { value: 'gray', label: 'Gray Base', color: '#64748B' },
    { value: 'brown', label: 'Brown Base', color: '#92400E' }
  ];

  if (!isOpen || !room) return null;

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-[1100] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">{room?.name}</h2>
            <p className="text-sm text-text-secondary mt-1">
              Preview how colors appear on this room background
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors duration-150"
            aria-label="Close preview"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
            <Image
              src={room?.backgroundImage}
              alt={room?.backgroundImageAlt}
              className="w-full h-full object-cover"
            />
            {selectedOverlay !== 'none' && (
              <div
                className="absolute inset-0 mix-blend-multiply opacity-30"
                style={{
                  backgroundColor: overlayOptions?.find(opt => opt?.value === selectedOverlay)?.color
                }}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Test Color Overlay
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {overlayOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => setSelectedOverlay(option?.value)}
                  className={`
                    p-3 border-2 rounded-lg transition-all duration-150
                    ${selectedOverlay === option?.value
                      ? 'border-primary bg-primary/5' :'border-border hover:border-muted'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    {option?.color && (
                      <div
                        className="w-6 h-6 rounded border border-border"
                        style={{ backgroundColor: option?.color }}
                      />
                    )}
                    {!option?.color && (
                      <div className="w-6 h-6 rounded border border-border bg-surface" />
                    )}
                    <span className="text-sm font-medium text-text-primary">
                      {option?.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-border">
            <div className="flex items-start gap-3">
              <Icon name="Tag" size={18} className="text-text-secondary mt-0.5" />
              <div>
                <div className="text-xs text-text-secondary">Category</div>
                <div className="text-sm font-medium text-text-primary">{room?.category}</div>
              </div>
            </div>

            {room?.dimensions && (
              <div className="flex items-start gap-3">
                <Icon name="Ruler" size={18} className="text-text-secondary mt-0.5" />
                <div>
                  <div className="text-xs text-text-secondary">Dimensions</div>
                  <div className="text-sm font-medium text-text-primary">{room?.dimensions}</div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Icon name="Users" size={18} className="text-text-secondary mt-0.5" />
              <div>
                <div className="text-xs text-text-secondary">Usage</div>
                <div className="text-sm font-medium text-text-primary">
                  {room?.usageCount} selections
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Close Preview
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomPreviewModal;